import { FaPlay, FaSave, FaTasks, FaDownload, FaImage, FaQuestionCircle, FaUpload } from 'react-icons/fa';
import { Button } from '../../../../ui/button';
import ContentCards from './ContentCards';
import useThemeColors from '../../../../hooks/useThemeColors';

const MessageBubble = ({ 
    message, 
    index, 
    openVideoUrl, 
    openHashtagModal, 
    openCaptionModal, 
    openTodoModal,
    downloadImage,
    onGenerateImage,
    onUploadImage
}) => {
    const { isDark } = useThemeColors();
    
    // Helper functions for content detection
    const hasHashtagsFunction = (message) => {
        return message.role === 'assistant' && 
               message.function_call && 
               message.function_call.name === 'generateHashtags';
    };
    
    const hasCaptionFunction = (message) => {
        return message.role === 'assistant' && 
               message.function_call && 
               message.function_call.name === 'generateCaption';
    };

    const hasTodoFunction = (message) => {
        return message.role === 'assistant' && 
               message.function_call && 
               message.function_call.name === 'generateTodo';
    };

    const hasVideoAnalysisFunction = (message) => {
        return message.role === 'assistant' && 
               message.function_call && 
               message.function_call.name === 'analyzeVideo';
    };

    const hasImageGenerationFunction = (message) => {
        return message.role === 'assistant' && 
               message.function_call && 
               message.function_call.name === 'generateImage';
    };

    const hasGeneratedImage = (message) => {
        return message.role === 'assistant' && message.generatedImage && message.generatedImage.url;
    };

    const isAmbiguousResponse = (message) => {
        return message.role === 'assistant' && 
               message.content && 
               message.content.toLowerCase().includes('not clear') &&
               message.content.toLowerCase().includes('image or text');
    };

    const containsHashtags = (message) => {
        if (!message.content) return false;
        return message.content.includes('#') && message.content.match(/#[\w\d]+/g);
    };

    const containsCaption = (message) => {
        if (!message.content) return false;
        return message.content.toLowerCase().includes('caption:') || 
               message.content.toLowerCase().includes('here is a caption') ||
               message.content.toLowerCase().includes('suggested caption');
    };

    const containsVideoAnalysis = (message) => {
        if (!message.content) return false;
        return message.videoAnalysis || 
               message.content.toLowerCase().includes('video analysis') ||
               message.content.toLowerCase().includes('key points') ||
               message.content.toLowerCase().includes('**video analysis:**');
    };

    // Check content types
    const hasHashtags = hasHashtagsFunction(message) || containsHashtags(message);
    const hasCaption = hasCaptionFunction(message) || containsCaption(message);
    const hasTodo = hasTodoFunction(message);
    const hasVideoAnalysis = hasVideoAnalysisFunction(message) || containsVideoAnalysis(message);
    const hasContentCards = message.contentCards && Array.isArray(message.contentCards);
    const hasImage = hasImageGenerationFunction(message) || hasGeneratedImage(message);
    const isAmbiguous = isAmbiguousResponse(message);

    return (
        <div key={index} className="mb-4">
            {/* Watch Video Button at top for AI messages with video analysis */}
          
            <div 
                className={`p-4 rounded-lg max-w-3xl ${
                    message.role === 'user' 
                        ? 'ml-auto bg-blue-500 !text-white' 
                        : '--theme-colors border --theme-colors-border'
                }`}
                style={{ whiteSpace: 'pre-wrap' }}
            >
                {message.content}
            </div>
            
            {/* Generated Image Display */}
            {hasImage && message.generatedImage && (
                <div className="mt-3">
                    <div className="border --theme-colors-border rounded-lg p-3 --theme-colors">
                        <img 
                            src={message.generatedImage.url} 
                            alt={message.generatedImage.prompt}
                            className="w-full max-w-md rounded-lg shadow-sm"
                            style={{ maxHeight: '400px', objectFit: 'contain' }}
                        />
                        <div className="mt-2 text-sm --theme-colors opacity-75">
                            <div><strong>Size:</strong> {message.generatedImage.size}</div>
                            <div><strong>Style:</strong> {message.generatedImage.style}</div>
                            {message.generatedImage.prompt && (
                                <div><strong>Prompt:</strong> {message.generatedImage.prompt}</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Content Cards Display */}
            {hasContentCards && <ContentCards contentCards={message.contentCards} />}

            {message.role === 'assistant' && message.videoAnalysis && (
                <div className="mt-2 flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openVideoUrl(message.videoAnalysis.videoUrl)}
                        className="flex items-center gap-1 text-xs"
                    >
                        <FaPlay className="h-3 w-3" />
                        Watch Video
                    </Button>
                </div>
            )}
            
            
            {/* Action buttons for AI-generated content */}
            {message.role === 'assistant' && (hasHashtags || hasCaption || hasTodo || hasImage || isAmbiguous) && (
                <div className="mt-2 flex flex-wrap gap-2">
                    {hasHashtags && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openHashtagModal(message)}
                            className="flex items-center gap-1 text-xs"
                        >
                            <FaSave className="h-3 w-3" />
                            Save Hashtags as Template
                        </Button>
                    )}
                    
                    {hasCaption && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openCaptionModal(message)}
                            className="flex items-center gap-1 text-xs"
                        >
                            <FaSave className="h-3 w-3" />
                            Save Caption as Template
                        </Button>
                    )}

                    {hasTodo && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openTodoModal(message)}
                            className="flex items-center gap-1 text-xs"
                        >
                            <FaTasks className="h-3 w-3" />
                            Add to Todo List
                        </Button>
                    )}

                    {hasImage && message.generatedImage && (
                        <>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => downloadImage(message.generatedImage.url, message.generatedImage.prompt)}
                                className="flex items-center gap-1 text-xs"
                            >
                                <FaDownload className="h-3 w-3" />
                                Download Image
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onUploadImage(message.generatedImage)}
                                className="flex items-center gap-1 text-xs bg-green-50 text-green-700 hover:bg-green-100"
                            >
                                <FaUpload className="h-3 w-3" />
                                Upload Image
                            </Button>
                        </>
                    )}

                    {isAmbiguous && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onGenerateImage(message.content)}
                            className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 hover:bg-blue-100"
                        >
                            <FaImage className="h-3 w-3" />
                            Generate Image
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default MessageBubble; 